import { useEffect, useState, type ElementType, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  AlertTriangle,
  CalendarDays,
  Camera,
  CheckCircle,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  FileText,
  Focus,
  Info,
  Languages,
  Lightbulb,
  Loader2,
  Maximize2,
  Pill,
  Plus,
  Printer,
  QrCode,
  Shield,
  Sparkles,
  Sun,
  Trash2,
  Utensils,
  Upload,
  Volume2,
  XCircle,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  analyzeVerifiedMedicines,
  calculateSafetyScore,
  generateSafetyIssues,
  getRiskExplanation,
} from '../services/openRouterService';
import { loadAnalysisResult, saveAnalysisResult } from '../services/analysisStorage';
import type { AiModelUsage, AnalysisResult, DrugInteraction, Medicine, PatientInfo, SafetyIssue } from '../types';
import '../styles/AnalysisResults.css';

type AnalysisPhase = 'extracting' | 'verifying' | 'finalizing' | 'complete';
type ReportStatus =
  | 'excellent-safe'
  | 'very-safe'
  | 'safe'
  | 'mostly-safe'
  | 'minimum-safe'
  | 'needs-attention'
  | 'high-risk'
  | 'dangerous';
type InstructionLanguage = 'en' | 'bn';
type ScheduleGroupKey = 'morning' | 'noon' | 'night' | 'beforeFood' | 'afterFood' | 'asNeeded';
type ScheduleTimeTab = 'morning' | 'noon' | 'evening' | 'night' | 'all';
type WarningTab = 'critical' | 'caution' | 'needsReview';

interface ScheduleGroup {
  key: ScheduleGroupKey;
  title: string;
  subtitle: string;
  icon: ReactNode;
  medicines: Medicine[];
}

interface ContextClue {
  title: string;
  description: string;
  medicines: string[];
}

const emptyMedicine: Medicine = {
  name: '',
  generic_name: '',
  genericName: '',
  strength: '',
  dosage: '',
  frequency: '',
  duration: '',
  timing: '',
  instruction: '',
  rawInstruction: '',
  notes: '',
  confidence: 0.8,
  genericConfirmed: false,
  verificationStatus: 'needs_verification',
};

interface SafetyEvidenceSummary {
  confirmedCriticalCount: number;
  confirmedModerateCount: number;
  confirmedDuplicateCount: number;
  possibleIssueCount: number;
  reviewMedicineCount: number;
  missingCriticalScheduleCount: number;
  missingDurationOnlyCount: number;
  lowConfidenceCount: number;
  dangerousInteractionCount: number;
}

const statusCopy: Record<ReportStatus, { label: string; title: string; summary: string; icon: ReactNode }> = {
  'excellent-safe': {
    label: 'Excellent Safe',
    title: 'Excellent Safe',
    summary: 'No major interaction or safety issue found. Prescription looks safe based on available data.',
    icon: <CheckCircle size={18} />,
  },
  'very-safe': {
    label: 'Very Safe',
    title: 'Very Safe',
    summary: 'No dangerous issue found. Minor missing information may exist, but overall looks safe.',
    icon: <Info size={18} />,
  },
  safe: {
    label: 'Safe',
    title: 'Safe',
    summary: 'Prescription appears safe. Some small caution or missing instruction may exist.',
    icon: <Info size={18} />,
  },
  'mostly-safe': {
    label: 'Mostly Safe',
    title: 'Mostly Safe',
    summary: 'No serious risk found, but user should read the notes carefully.',
    icon: <Info size={18} />,
  },
  'minimum-safe': {
    label: 'Minimum Safe',
    title: 'Minimum Safe',
    summary: 'No confirmed dangerous interaction found, but data quality or medicine clarity is limited.',
    icon: <AlertTriangle size={18} />,
  },
  'needs-attention': {
    label: 'Needs Attention',
    title: 'Needs Attention',
    summary: 'Possible safety concern found. User should confirm with doctor/pharmacist before taking.',
    icon: <AlertTriangle size={18} />,
  },
  'high-risk': {
    label: 'High Risk',
    title: 'High Risk',
    summary: 'Strong warning. Interaction, allergy, dosage, or duplicate medicine risk may exist.',
    icon: <XCircle size={18} />,
  },
  dangerous: {
    label: 'Dangerous',
    title: 'Dangerous',
    summary: 'Serious safety risk detected. User should not rely on this prescription without urgent medical/pharmacist confirmation.',
    icon: <XCircle size={18} />,
  },
};

function isMissingValue(value?: string): boolean {
  const normalized = (value || '').trim().toLowerCase();
  return !normalized || ['not specified', 'needs review', 'needs verification', 'unknown', 'n/a', 'na'].includes(normalized);
}

function isActualMedicine(medicine: Medicine): boolean {
  const text = [
    medicine.name,
    medicine.form,
    medicine.category,
    medicine.commonUse,
    medicine.notes,
    medicine.rawInstruction,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!text.trim()) return false;
  if (/\b(cbc|sgpt|hba1c|crp|mt test|urine|electrolyte|investigation|advice|bp|pulse|temperature|spo2|diagnosis|symptom)\b/.test(text)) {
    return false;
  }

  return true;
}

function getConfirmedDuplicateCount(issues: SafetyIssue[]): number {
  return issues.filter((issue) => issue.title === 'Confirmed duplicate ingredient').length;
}

function getSafetyEvidenceSummary(
  medicines: Medicine[],
  interactions: DrugInteraction[],
  issues: SafetyIssue[]
): SafetyEvidenceSummary {
  const confirmedCriticalCount = interactions.filter(
    (interaction) =>
      interaction.status === 'confirmed_interaction' &&
      (interaction.severity === 'critical' || interaction.severity === 'high')
  ).length;
  const confirmedModerateCount = interactions.filter(
    (interaction) => interaction.status === 'confirmed_interaction' && interaction.severity === 'moderate'
  ).length;
  const possibleIssueCount = issues.filter(
    (issue) => issue.status === 'possible_issue' && issue.title !== 'Confirmed duplicate ingredient'
  ).length;
  const confirmedDuplicateCount = getConfirmedDuplicateCount(issues);
  const reviewMedicineCount = medicines.filter(
    (medicine) =>
      medicine.identificationStatus === 'needs_review' ||
      medicine.verificationStatus === 'needs_verification' ||
      isMissingValue(medicine.generic_name || medicine.genericName)
  ).length;
  const missingCriticalScheduleCount = medicines.filter(
    (medicine) => isMissingValue(medicine.dosage) || isMissingValue(medicine.frequency) || isMissingValue(medicine.timing)
  ).length;
  const missingDurationOnlyCount = medicines.filter(
    (medicine) =>
      !isMissingValue(medicine.dosage) &&
      !isMissingValue(medicine.frequency) &&
      !isMissingValue(medicine.timing) &&
      isMissingValue(medicine.duration)
  );

  return {
    confirmedCriticalCount,
    confirmedModerateCount,
    confirmedDuplicateCount,
    possibleIssueCount,
    reviewMedicineCount,
    missingCriticalScheduleCount,
    missingDurationOnlyCount: missingDurationOnlyCount.length,
    lowConfidenceCount: medicines.filter((medicine) => (medicine.confidence ?? 1) < 0.7).length,
    dangerousInteractionCount: confirmedCriticalCount,
  };
}

function getReportStatus(score: number): ReportStatus {
  if (score >= 90) return 'excellent-safe';
  if (score >= 80) return 'very-safe';
  if (score >= 70) return 'safe';
  if (score >= 60) return 'mostly-safe';
  if (score >= 50) return 'minimum-safe';
  if (score >= 35) return 'needs-attention';
  if (score >= 20) return 'high-risk';
  return 'dangerous';
}

function getScoreColor(score: number): string {
  if (score >= 90) return '#0d9488';
  if (score >= 80) return '#0284c7';
  if (score >= 70) return '#2563eb';
  if (score >= 60) return '#ca8a04';
  if (score >= 50) return '#ea580c';
  if (score >= 35) return '#f97316';
  if (score >= 20) return '#dc2626';
  return '#991b1b';
}

function getScoreExplanation(evidence: SafetyEvidenceSummary): string {
  if (evidence.confirmedCriticalCount > 0) {
    return 'Score is capped because a confirmed serious interaction was detected.';
  }
  if (evidence.confirmedDuplicateCount > 0) {
    return 'Score is capped because a confirmed duplicate active ingredient was detected.';
  }
  if (evidence.confirmedModerateCount > 0) {
    return 'Score reflects confirmed moderate interaction evidence that needs professional review.';
  }
  if (evidence.possibleIssueCount > 0) {
    return 'Score reflects possible interaction or look-alike findings that should be checked.';
  }
  if (evidence.reviewMedicineCount > 0) {
    return 'Score reflects medicine details that still need confirmation from the strip, box, doctor, or pharmacist.';
  }
  if (evidence.missingCriticalScheduleCount > 0 || evidence.missingDurationOnlyCount > 0 || evidence.lowConfidenceCount > 0) {
    return 'Score reflects minor missing or low-confidence fields, with no confirmed dangerous interaction detected.';
  }
  return 'Score is based on confirmed medicine identity, schedule clarity, and local interaction checks.';
}

function getDefaultScheduleTab(date = new Date()): ScheduleTimeTab {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 16) return 'noon';
  if (hour >= 16 && hour < 20) return 'evening';
  return 'night';
}

function normalizeBanglaDigits(text: string): string {
  return text.replace(/[\u09E6-\u09EF]/g, (digit) => String(digit.charCodeAt(0) - 0x09E6));
}

function parseThreePartFrequency(text: string): number[] | null {
  const match = normalizeBanglaDigits(text).match(/([0-9]+)\s*\+\s*([0-9]+)\s*\+\s*([0-9]+)/);
  return match ? match.slice(1).map((value) => Number(value)) : null;
}

function getMedicineTimeTabs(medicine: Medicine): ScheduleTimeTab[] {
  const text = normalizeInstructionSearch(medicine);
  const tabs = new Set<ScheduleTimeTab>();
  const frequency = parseThreePartFrequency(text);

  if (frequency) {
    if (frequency[0] > 0) tabs.add('morning');
    if (frequency[1] > 0) tabs.add('noon');
    if (frequency[2] > 0) tabs.add('night');
  }

  if (/morning|breakfast|\u09B8\u0995\u09BE\u09B2|\u09A8\u09BE\u09B8\u09CD\u09A4\u09BE/.test(text)) tabs.add('morning');
  if (/noon|lunch|\u09A6\u09C1\u09AA\u09C1\u09B0/.test(text)) tabs.add('noon');
  if (/evening|\u09B8\u09A8\u09CD\u09A7\u09CD\u09AF\u09BE|\u09AC\u09BF\u0995\u09BE\u09B2/.test(text)) tabs.add('evening');
  if (/night|bedtime|\u09B0\u09BE\u09A4|\u09B0\u09BE\u09A4\u09C7/.test(text)) tabs.add('night');

  if (/every\s*8\s*hours?|8\s*hours?\s*apart/.test(text)) {
    tabs.add('morning');
    tabs.add('noon');
    tabs.add('evening');
    tabs.add('night');
  }

  if (/every\s*12\s*hours?|12\s*hours?\s*apart/.test(text)) {
    tabs.add('morning');
    tabs.add('night');
  }

  return Array.from(tabs);
}

function getMedicinesForTimeTab(medicines: Medicine[], tab: ScheduleTimeTab): Medicine[] {
  if (tab === 'all') return medicines;
  return medicines.filter((medicine) => getMedicineTimeTabs(medicine).includes(tab));
}

function getInstructionText(medicine: Medicine, language: InstructionLanguage): string {
  if (language === 'bn') {
    return (
      medicine.banglaInstruction ||
      'ডোজ, সময়, খাবারের আগে/পরে এবং কতদিন খাবেন তা ডাক্তার বা ফার্মাসিস্টের সাথে নিশ্চিত করুন।'
    );
  }

  return (
    medicine.englishInstruction ||
    medicine.instruction ||
    'Instruction needs review. Confirm dose, frequency, timing, and duration with a doctor or pharmacist.'
  );
}

function getScheduleText(medicine: Medicine): string {
  return displayValue(medicine.frequencyLabel || medicine.frequency, 'Schedule needs review');
}

function getFoodTimingText(medicine: Medicine): string {
  return displayValue(medicine.timingLabel || medicine.timing, 'Food timing not provided');
}

function hasDuration(medicine: Medicine): boolean {
  return Boolean((medicine.durationLabel || medicine.duration || '').trim());
}

function normalizeInstructionSearch(medicine: Medicine): string {
  return [
    medicine.name,
    medicine.generic_name,
    medicine.genericName,
    medicine.category,
    medicine.commonUse,
    medicine.riskNotes,
    medicine.frequency,
    medicine.frequencyLabel,
    medicine.timing,
    medicine.timingLabel,
    medicine.duration,
    medicine.durationLabel,
    medicine.instruction,
    medicine.englishInstruction,
    medicine.banglaInstruction,
    medicine.rawInstruction,
    medicine.notes,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isAsNeededOrContinue(medicine: Medicine): boolean {
  const text = normalizeInstructionSearch(medicine);
  return /if|as needed|when needed|continue|as advised|চলবে|জ্বর হলে|ব্যথা হলে/.test(text);
}

function medicineMatchesGroup(medicine: Medicine, key: ScheduleGroupKey): boolean {
  const text = normalizeInstructionSearch(medicine);

  if (key === 'morning') return /morning|breakfast|1\+0\+|সকাল|নাস্তা/.test(text);
  if (key === 'noon') return /noon|lunch|0\+1\+|1\+1\+|দুপুর/.test(text);
  if (key === 'night') return /night|bedtime|\+1\b|রাত|রাতে/.test(text);
  if (key === 'beforeFood') return /before food|before meal|খাবারের আগে|খাবার আগে|পূর্বে/.test(text);
  if (key === 'afterFood') return /after food|after meal|খাবারের পরে|খাবার পরে|খাবার পর/.test(text);
  return isAsNeededOrContinue(medicine);
}

function buildScheduleGroups(medicines: Medicine[]): ScheduleGroup[] {
  const configs: Array<Omit<ScheduleGroup, 'medicines'>> = [
    { key: 'morning', title: 'Morning', subtitle: 'Medicines with morning or breakfast timing', icon: <Sun size={16} /> },
    { key: 'noon', title: 'Noon', subtitle: 'Medicines with noon or lunch timing', icon: <Clock3 size={16} /> },
    { key: 'night', title: 'Night', subtitle: 'Medicines with night or bedtime timing', icon: <Clock3 size={16} /> },
    { key: 'beforeFood', title: 'Before food', subtitle: 'Take before meal timing only when prescribed', icon: <Utensils size={16} /> },
    { key: 'afterFood', title: 'After food', subtitle: 'Take after meal timing only when prescribed', icon: <Utensils size={16} /> },
    { key: 'asNeeded', title: 'As needed / Continue', subtitle: 'Conditional or continuing instructions', icon: <CalendarDays size={16} /> },
  ];

  return configs.map((config) => ({
    ...config,
    medicines: medicines.filter((medicine) => medicineMatchesGroup(medicine, config.key)),
  }));
}

function getInstructionBadges(medicine: Medicine): Array<{ label: string; tone: string }> {
  const status = getMedicineStatus(medicine);
  const badges: Array<{ label: string; tone: string }> = [{ label: status.label, tone: status.tone }];
  const timingText = normalizeInstructionSearch(medicine);

  if (!hasDuration(medicine)) {
    badges.push({ label: 'Missing Duration', tone: 'missing' });
  }

  if (/after food|after meal|খাবারের পরে|খাবার পরে|খাবার পর/.test(timingText)) {
    badges.push({ label: 'After Food', tone: 'after' });
  }

  if (/before food|before meal|খাবারের আগে|খাবার আগে|পূর্বে/.test(timingText)) {
    badges.push({ label: 'Before Food', tone: 'before' });
  }

  return badges;
}

function getPossiblePrescriptionContext(medicines: Medicine[]): ContextClue[] {
  const contextMap: Array<{ title: string; description: string; test: RegExp }> = [
    {
      title: 'Diabetes / blood sugar control clue',
      description: 'This prescription may be related to blood sugar control because diabetes medicines or insulin are present.',
      test: /diabetes|insulin|metformin|glimepiride|blood glucose|blood sugar/i,
    },
    {
      title: 'Blood pressure management clue',
      description: 'This prescription may include blood pressure or heart-rate management medicines.',
      test: /blood pressure|losartan|amlodipine|metoprolol|heart rate|cardiac/i,
    },
    {
      title: 'Infection or antibiotic treatment clue',
      description: 'This prescription may include treatment for an infection because an antibiotic is present.',
      test: /antibiotic|doxycycline|metronidazole|infection/i,
    },
    {
      title: 'Gastric protection clue',
      description: 'This prescription may include stomach protection or acid-reducer support.',
      test: /gastric|acid|reflux|omeprazole|esomeprazole|rabeprazole/i,
    },
    {
      title: 'Breathing / allergy support clue',
      description: 'This prescription may include breathing, asthma, allergy, or nebulization support.',
      test: /respiratory|asthma|allergy|montelukast|doxofylline|nebul/i,
    },
    {
      title: 'Pain / nerve-related clue',
      description: 'This prescription may include pain, fever, sleep, seizure, or nerve-related support.',
      test: /pain|fever|paracetamol|pregabalin|clonazepam|sedative|neurology|nerve/i,
    },
  ];

  return contextMap
    .map((context) => {
      const matched = medicines.filter((medicine) => context.test.test(normalizeInstructionSearch(medicine)));
      return { title: context.title, description: context.description, medicines: matched.map((medicine) => medicine.name) };
    })
    .filter((context) => context.medicines.length > 0);
}

function getDoctorChecklist(medicines: Medicine[]): Array<{ title: string; items: string[] }> {
  const needsVerification = medicines
    .filter((medicine) => medicine.identificationStatus !== 'identified')
    .map((medicine) => medicine.name);
  const missingDuration = medicines
    .filter((medicine) => !hasDuration(medicine))
    .map((medicine) => medicine.name);
  const doNotStopWithoutAdvice = medicines
    .filter((medicine) => /do not stop|sedative|insulin|blood pressure|heart|seizure|clonazepam|pregabalin|metoprolol/i.test(normalizeInstructionSearch(medicine)))
    .map((medicine) => medicine.name);
  const confirmInstructions = medicines
    .filter((medicine) => !medicine.dosage || !medicine.frequency || !medicine.timing)
    .map((medicine) => medicine.name);

  return [
    {
      title: 'Medicines needing verification',
      items: needsVerification.length ? needsVerification : ['No unresolved medicine names found in this report.'],
    },
    {
      title: 'Duration to confirm',
      items: missingDuration.length ? missingDuration : ['All detected medicines include a duration or continue-as-advised note.'],
    },
    {
      title: 'Ask before changing or stopping',
      items: doNotStopWithoutAdvice.length
        ? doNotStopWithoutAdvice
        : ['No specific stop-warning clue found. Still follow your doctor’s prescription.'],
    },
    {
      title: 'Instructions to confirm',
      items: confirmInstructions.length
        ? confirmInstructions
        : ['Dose, schedule, and timing are present for the detected medicines.'],
    },
  ];
}

function speakInstruction(text: string, language: InstructionLanguage) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = language === 'bn' ? 'bn-BD' : 'en-US';
  utterance.rate = language === 'bn' ? 0.86 : 0.9;
  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
}

function buildQrLabelText(medicine: Medicine, language: InstructionLanguage): string {
  const labelText = [
    `MedCheck label: ${medicine.name}`,
    `Generic: ${medicine.generic_name || medicine.genericName || 'Needs review'}`,
    `Schedule: ${getScheduleText(medicine)}`,
    `Food: ${getFoodTimingText(medicine)}`,
    `Duration: ${displayValue(medicine.durationLabel || medicine.duration, 'Needs review')}`,
    `Instruction: ${getInstructionText(medicine, language)}`,
    'Confirm with doctor/pharmacist before use.',
  ].join('\n');

  return labelText.length > 700 ? `${labelText.slice(0, 697)}...` : labelText;
}

export const AnalysisResults: React.FC = () => {
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [phase, setPhase] = useState<AnalysisPhase>('extracting');
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [ocrText, setOcrText] = useState('');
  const [draftMedicines, setDraftMedicines] = useState<Medicine[]>([]);
  const [modelUsage, setModelUsage] = useState<AiModelUsage>({});
  const location = useLocation();
  const navigate = useNavigate();

  const steps = [
    { label: 'Reading Prescription', icon: FileText },
    { label: 'Organizing Medicines', icon: Pill },
    { label: 'Preparing Safety Review', icon: Shield },
    { label: 'Building Schedule', icon: CheckCircle },
  ];

  useEffect(() => {
    const state = location.state as {
      analysisId?: string;
      reviewMode?: boolean;
      draftMedicines?: Medicine[];
      ocrText?: string;
      message?: string;
      analysis?: AnalysisResult;
    } | null;

    const savedAnalysis = state?.analysis || loadAnalysisResult(state?.analysisId);
    if (savedAnalysis) {
      setAnalysis(savedAnalysis);
      setOcrText(savedAnalysis.ocrText || savedAnalysis.extractedText || '');
      setModelUsage(savedAnalysis.modelUsage || {});
      setPhase('complete');
      setCurrentStep(3);
      return;
    }

    if (state?.reviewMode) {
      setOcrText(state.ocrText || '');
      setDraftMedicines(state.draftMedicines?.length ? state.draftMedicines : [{ ...emptyMedicine }]);
      setModelUsage({});
      setError(null);
      setCurrentStep(2);
      setPhase('verifying');
      return;
    }

    setError('We could not read this prescription clearly. Please retake the photo or enter medicines manually.');
    setPhase('verifying');
  }, [location.state]);

  const startManualEntry = () => {
    setError(null);
    setOcrText('');
    setDraftMedicines([{ ...emptyMedicine }]);
    setModelUsage({});
    setCurrentStep(2);
    setPhase('verifying');
  };

  const updateMedicine = (index: number, field: keyof Medicine, value: string) => {
    setDraftMedicines((medicines) =>
      medicines.map((medicine, medicineIndex) => {
        if (medicineIndex !== index) return medicine;
        const nextMedicine = { ...medicine, [field]: value };
        if (field === 'generic_name') {
          nextMedicine.genericName = value;
        }
        return nextMedicine;
      })
    );
  };

  const addMedicine = () => {
    setDraftMedicines((medicines) => [...medicines, { ...emptyMedicine }]);
  };

  const removeMedicine = (index: number) => {
    setDraftMedicines((medicines) => medicines.filter((_, medicineIndex) => medicineIndex !== index));
  };

  const runFinalAnalysis = async () => {
    try {
      setError(null);
      setPhase('finalizing');
      setCurrentStep(3);
      const result = await analyzeVerifiedMedicines(draftMedicines, ocrText, modelUsage);
      const analysisId = (location.state as { analysisId?: string } | null)?.analysisId;
      if (analysisId) {
        saveAnalysisResult(analysisId, result);
      }
      setAnalysis(result);
      setModelUsage(result.modelUsage || modelUsage);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete safety analysis');
      setPhase('verifying');
      setCurrentStep(2);
    }
  };

  if (error) {
    return <AnalysisError error={error} onRetry={() => navigate('/upload')} onManualEntry={startManualEntry} />;
  }

  if (phase === 'extracting' || phase === 'finalizing') {
    return (
      <LoadingAnalysis
        currentStep={currentStep}
        steps={steps}
        title={phase === 'extracting' ? 'Reading Your Prescription' : 'Preparing Medical Report'}
        description={
          phase === 'extracting'
            ? 'MedCheck is extracting medicine details for your review.'
            : 'MedCheck is checking safety evidence and verification gaps.'
        }
      />
    );
  }

  if (phase === 'verifying') {
    return (
      <VerificationScreen
        medicines={draftMedicines}
        ocrText={ocrText}
        onChange={updateMedicine}
        onAdd={addMedicine}
        onRemove={removeMedicine}
        onConfirm={runFinalAnalysis}
        onBack={() => navigate('/upload')}
      />
    );
  }

  if (!analysis) {
    return (
      <AnalysisError
        error="No analysis result available"
        onRetry={() => navigate('/upload')}
        onManualEntry={startManualEntry}
      />
    );
  }

  const issues = analysis.issues || generateSafetyIssues(analysis.medicines, analysis.interactions);
  const reportMedicines = analysis.medicines.filter(isActualMedicine);
  const recalculatedScore = calculateSafetyScore(reportMedicines, analysis.interactions);
  const evidence = getSafetyEvidenceSummary(reportMedicines, analysis.interactions, issues);
  const reportStatus = getReportStatus(recalculatedScore);
  const riskInfo = getRiskExplanation(reportMedicines, analysis.interactions, recalculatedScore, issues);
  const identifiedCount = reportMedicines.filter((medicine) => medicine.identificationStatus === 'identified').length;
  const reviewMedicineCount = evidence.reviewMedicineCount;
  const verificationCount = reviewMedicineCount;

  return (
    <div className="analysis-results">
      <div className="results-container report-container">
        <motion.div
          className="report-kicker"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Shield size={16} />
          <span>MedCheck Safety Report</span>
        </motion.div>

        <PrescriptionInfoCard patientInfo={analysis.patientInfo} />

        <SafetySummaryCard
          score={recalculatedScore}
          status={reportStatus}
          medicinesCount={reportMedicines.length}
          identifiedCount={identifiedCount}
          reviewMedicineCount={reviewMedicineCount}
          confirmedInteractions={evidence.dangerousInteractionCount}
          verificationCount={verificationCount}
          evidence={evidence}
        />

        <TimeBasedScheduleTabs medicines={analysis.medicines} />

        <TabbedPatientInstructionsSection medicines={analysis.medicines} />

        <WarningsTabs issues={issues} />

        <div className="compact-two-column">
          <SmartActionsPanel analysis={analysis} />
          <DoctorChecklistSection medicines={analysis.medicines} />
        </div>

        <FullDetailsAccordion
          medicines={analysis.medicines}
          issues={issues}
          interactions={analysis.interactions}
          patientInfo={analysis.patientInfo}
          ocrText={ocrText || analysis.ocrText || ''}
          tips={riskInfo.tips}
          status={reportStatus}
        />

        <DisclaimerPanel />
      </div>
    </div>
  );
};

const SafetySummaryCard: React.FC<{
  score: number;
  status: ReportStatus;
  medicinesCount: number;
  identifiedCount: number;
  reviewMedicineCount: number;
  confirmedInteractions: number;
  verificationCount: number;
  evidence: SafetyEvidenceSummary;
}> = ({ score, status, medicinesCount, identifiedCount, reviewMedicineCount, confirmedInteractions, evidence }) => {
  const copy = statusCopy[status];
  const color = getScoreColor(score);
  const explanation = getScoreExplanation(evidence);
  const countLine = `${medicinesCount} medicines detected · ${identifiedCount} identified · ${reviewMedicineCount} need review · ${confirmedInteractions} dangerous interactions`;

  return (
    <motion.section
      className={`report-summary-card ${status}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="report-score-wrap">
        <div className="score-circle-container premium">
          <svg className="score-ring" viewBox="0 0 100 100">
            <circle className="score-ring-bg" cx="50" cy="50" r="44" />
            <motion.circle
              className="score-ring-fill"
              cx="50"
              cy="50"
              r="44"
              style={{ stroke: color }}
              initial={{ strokeDashoffset: 276 }}
              animate={{ strokeDashoffset: 276 - (276 * score) / 100 }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
            />
          </svg>
          <div className="score-value">
            <span className="score-number">{Math.round(score)}</span>
            <span className="score-max">/100</span>
          </div>
        </div>
      </div>

      <div className="report-summary-copy">
        <div className={`report-status-badge ${status}`}>
          {copy.icon}
          <span>{copy.label}</span>
        </div>
        <h1>{copy.title}</h1>
        <p>{copy.summary}</p>
        <div className="report-countline">
          {countLine}
        </div>
        <p className="score-explanation">{explanation}</p>

        <div className="summary-metrics">
          <div>
            <strong>{medicinesCount}</strong>
            <span>Medicines detected</span>
          </div>
          <div>
            <strong>{identifiedCount}</strong>
            <span>Identified</span>
          </div>
          <div>
            <strong>{reviewMedicineCount}</strong>
            <span>Need review</span>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

const PrescriptionInfoCard: React.FC<{ patientInfo?: PatientInfo }> = ({ patientInfo }) => {
  const visibleItems = [
    patientInfo?.patientName ? { label: 'Patient', value: patientInfo.patientName } : null,
    patientInfo?.age ? { label: 'Age', value: patientInfo.age } : null,
    patientInfo?.date ? { label: 'Date', value: patientInfo.date } : null,
    patientInfo?.doctorName ? { label: 'Doctor', value: patientInfo.doctorName } : null,
    patientInfo?.hospitalName ? { label: 'Chamber', value: patientInfo.hospitalName } : null,
  ].filter((item): item is { label: string; value: string } => Boolean(item));

  if (visibleItems.length === 0) return null;

  return (
    <section className="prescription-info-card">
      <div className="prescription-info-title">
        <FileText size={17} />
        <span>Prescription Info</span>
      </div>
      <div className="prescription-info-grid">
        {visibleItems.map((item) => (
          <div key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
};

const TimeBasedScheduleTabs: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => {
  const [activeTab, setActiveTab] = useState<ScheduleTimeTab>(getDefaultScheduleTab());
  const tabs: Array<{ key: ScheduleTimeTab; label: string; title: string }> = [
    { key: 'morning', label: 'Morning', title: 'Morning Medicines' },
    { key: 'noon', label: 'Noon', title: 'Noon Medicines' },
    { key: 'evening', label: 'Evening', title: 'Evening Medicines' },
    { key: 'night', label: 'Night', title: 'Night Medicines' },
    { key: 'all', label: 'All', title: 'All Medicines' },
  ];
  const activeConfig = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const visibleMedicines = getMedicinesForTimeTab(medicines, activeTab);

  return (
    <motion.section
      className="report-section compact-schedule-section"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 }}
    >
      <SectionHeader
        icon={<Clock3 size={20} />}
        title="Time-Based Schedule"
        subtitle="Shows only the medicines for the selected time"
        meta={activeConfig.title}
      />

      <div className="dashboard-tabs sticky-tabs" role="tablist" aria-label="Medicine time">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {visibleMedicines.length > 0 ? (
        <div className="compact-medicine-grid">
          {visibleMedicines.map((medicine, index) => (
            <CompactMedicineCard key={`${activeTab}-${medicine.name}-${index}`} medicine={medicine} />
          ))}
        </div>
      ) : (
        <div className="compact-empty-state">
          <CheckCircle size={22} />
          <strong>No medicine scheduled for this time.</strong>
          <p>Switch tabs to review the rest of the prescription.</p>
        </div>
      )}
    </motion.section>
  );
};

const CompactMedicineCard: React.FC<{ medicine: Medicine }> = ({ medicine }) => {
  const status = getMedicineStatus(medicine);
  const duration = displayValue(medicine.durationLabel || medicine.duration, 'Duration needs review');

  return (
    <article className="compact-medicine-card">
      <div className="compact-medicine-heading">
        <h3>{medicine.name || 'Unnamed medicine'}</h3>
        <span className={`medicine-status ${status.tone}`}>{status.label}</span>
      </div>
      <p>{getInstructionText(medicine, 'en')}</p>
      <div className="compact-card-badges">
        <span>{duration}</span>
        {medicine.timingLabel || medicine.timing ? <span>{medicine.timingLabel || medicine.timing}</span> : null}
      </div>
    </article>
  );
};

const TabbedPatientInstructionsSection: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => {
  const [language, setLanguage] = useState<InstructionLanguage>('en');

  return (
    <motion.section
      className="report-section compact-instructions-section"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
    >
      <SectionHeader
        icon={<Languages size={20} />}
        title="Patient Instructions"
        subtitle="Simple patient-friendly guidance"
        meta={language === 'bn' ? 'বাংলা' : 'English'}
      />

      <div className="dashboard-tabs" role="tablist" aria-label="Instruction language">
        <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')} type="button">
          English
        </button>
        <button className={language === 'bn' ? 'active' : ''} onClick={() => setLanguage('bn')} type="button">
          বাংলা
        </button>
      </div>

      <div className="compact-instruction-list">
        {medicines.map((medicine, index) => (
          <article className="compact-instruction-card" key={`${language}-${medicine.name}-${index}`}>
            <div className="instruction-number compact">{index + 1}</div>
            <div>
              <h3>{medicine.name || 'Unnamed medicine'}</h3>
              <p className={language === 'bn' ? 'bangla-text' : ''}>{getInstructionText(medicine, language)}</p>
            </div>
            <button
              className="icon-action-button"
              onClick={() => speakInstruction(getInstructionText(medicine, language), language)}
              type="button"
              aria-label={`Play instruction for ${medicine.name}`}
            >
              <Volume2 size={16} />
            </button>
          </article>
        ))}
      </div>
    </motion.section>
  );
};

function getWarningBuckets(issues: SafetyIssue[]) {
  const critical = issues.filter(
    (issue) =>
      issue.status === 'confirmed_interaction' &&
      (issue.severity === 'critical' || issue.severity === 'high')
  );
  const needsReview = issues.filter(
    (issue) => issue.status === 'needs_verification' || issue.status === 'missing_instruction'
  );
  const caution = issues.filter((issue) => !critical.includes(issue) && !needsReview.includes(issue));

  return { critical, caution, needsReview };
}

const WarningsTabs: React.FC<{ issues: SafetyIssue[] }> = ({ issues }) => {
  const buckets = getWarningBuckets(issues);
  const initialTab: WarningTab =
    buckets.critical.length > 0 ? 'critical' : buckets.caution.length > 0 ? 'caution' : 'needsReview';
  const [activeTab, setActiveTab] = useState<WarningTab>(initialTab);
  const tabs: Array<{ key: WarningTab; label: string; count: number }> = [
    { key: 'critical', label: 'Critical', count: buckets.critical.length },
    { key: 'caution', label: 'Caution', count: buckets.caution.length },
    { key: 'needsReview', label: 'Needs Review', count: buckets.needsReview.length },
  ];
  const activeIssues = buckets[activeTab];

  return (
    <motion.section
      className="report-section compact-warnings-section"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.16 }}
    >
      <SectionHeader
        icon={<AlertTriangle size={20} />}
        title="Warnings"
        subtitle="Grouped evidence, not repeated cards"
        meta={issues.length === 0 ? 'Clear' : `${issues.length} total`}
      />

      <div className="dashboard-tabs warning-tabs" role="tablist" aria-label="Warning type">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
            <span>{tab.count}</span>
          </button>
        ))}
      </div>

      {activeIssues.length === 0 ? (
        <div className="compact-empty-state">
          <CheckCircle size={22} />
          <strong>
            {activeTab === 'critical' ? 'No confirmed serious risk found.' : 'No issue in this group.'}
          </strong>
          <p>No confirmed dangerous interaction found unless shown in the Critical tab.</p>
        </div>
      ) : activeTab === 'needsReview' ? (
        <GroupedReviewIssues issues={activeIssues} />
      ) : (
        <div className="compact-warning-list">
          {activeIssues.map((issue, index) => (
            <article className={`compact-warning-card ${activeTab}`} key={`${issue.title}-${index}`}>
              <strong>{issue.title}</strong>
              <p>{issue.description}</p>
            </article>
          ))}
        </div>
      )}
    </motion.section>
  );
};

const GroupedReviewIssues: React.FC<{ issues: SafetyIssue[] }> = ({ issues }) => {
  const medicines = Array.from(new Set(issues.flatMap((issue) => issue.medicines || [])));
  const missing = issues.filter((issue) => issue.status === 'missing_instruction').length;
  const verification = issues.filter((issue) => issue.status === 'needs_verification').length;

  return (
    <article className="grouped-review-card">
      <div>
        <strong>{medicines.length || issues.length} medicine{(medicines.length || issues.length) === 1 ? '' : 's'} need review</strong>
        <p>
          {verification > 0 ? `${verification} active ingredient check${verification === 1 ? '' : 's'}` : 'No active ingredient review items'}
          {missing > 0 ? ` · ${missing} missing instruction item${missing === 1 ? '' : 's'}` : ''}
        </p>
      </div>
      {medicines.length > 0 && (
        <div className="medicine-chip-list compact">
          {medicines.map((name) => <span key={name}>{name}</span>)}
        </div>
      )}
    </article>
  );
};

const SmartActionsPanel: React.FC<{ analysis: AnalysisResult }> = ({ analysis }) => {
  const navigate = useNavigate();

  return (
    <motion.section
      className="report-section smart-actions-panel"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <SectionHeader
        icon={<QrCode size={20} />}
        title="Smart QR / Voice Guide"
        subtitle="Generate one QR schedule and voice instructions"
      />
      <div className="smart-action-buttons">
        <button className="btn btn-primary" onClick={() => navigate('/audio', { state: { analysis } })} type="button">
          <QrCode size={18} />
          QR & Voice Guide
        </button>
        <button className="btn btn-outline" onClick={() => navigate('/upload')} type="button">
          <Upload size={18} />
          New Analysis
        </button>
      </div>
    </motion.section>
  );
};

const DoctorChecklistSection: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => {
  const checklist = getDoctorChecklist(medicines);

  return (
    <motion.section
      className="report-section compact-doctor-checklist"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.22 }}
    >
      <SectionHeader
        icon={<ClipboardCheck size={20} />}
        title="Doctor Checklist"
        subtitle="Quick items to confirm"
      />
      <div className="compact-checklist-list">
        {checklist.map((section) => (
          <details key={section.title} className="compact-checklist-group">
            <summary>{section.title}</summary>
            {section.items.map((item) => (
              <label key={`${section.title}-${item}`}>
                <input type="checkbox" />
                <span>{item}</span>
              </label>
            ))}
          </details>
        ))}
      </div>
    </motion.section>
  );
};

const FullDetailsAccordion: React.FC<{
  medicines: Medicine[];
  issues: SafetyIssue[];
  interactions: DrugInteraction[];
  patientInfo?: PatientInfo;
  ocrText: string;
  tips: string[];
  status: ReportStatus;
}> = ({ medicines, issues, interactions, patientInfo, ocrText, tips, status }) => (
  <details className="full-details-accordion">
    <summary>
      <span>Full Details</span>
      <small>Medicines, safety evidence, extracted text</small>
    </summary>
    <div className="full-details-content">
      <div className="expanded-patient-info">
        <h3>Prescription Info</h3>
        <div>
          <span>Patient: {patientInfo?.patientName || 'Not detected'}</span>
          <span>Age: {patientInfo?.age || 'Not detected'}</span>
          <span>Date: {patientInfo?.date || 'Not detected'}</span>
          <span>Doctor: {patientInfo?.doctorName || 'Not detected'}</span>
          <span>Chamber: {patientInfo?.hospitalName || 'Not detected'}</span>
        </div>
      </div>

      <KeyFindingsPanel issues={issues} status={status} />
      <MedicinesSection medicines={medicines} />
      {interactions.length > 0 && <InteractionsSection interactions={interactions} />}
      <NextStepsPanel tips={tips} status={status} />
      {ocrText && (
        <details className="ocr-review compact-ocr-review">
          <summary>Extracted prescription text</summary>
          <p>{ocrText}</p>
        </details>
      )}
    </div>
  </details>
);

const KeyFindingsPanel: React.FC<{ issues: SafetyIssue[]; status: ReportStatus }> = ({ issues, status }) => {
  const criticalIssues = issues.filter(
    (issue) =>
      issue.status === 'confirmed_interaction' &&
      (issue.severity === 'critical' || issue.severity === 'high')
  );
  const activeIngredientIssues = issues.filter((issue) => issue.status === 'needs_verification');
  const missingInstructionIssues = issues.filter((issue) => issue.status === 'missing_instruction');
  const cautionIssues = issues.filter(
    (issue) =>
      !criticalIssues.includes(issue) &&
      issue.status !== 'needs_verification' &&
      issue.status !== 'missing_instruction'
  );

  return (
    <motion.section
      className="report-section insight-panel"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 }}
    >
      <SectionHeader
        icon={<AlertCircle size={20} />}
        title="Key Findings"
        subtitle="Safety evidence grouped for quick review"
        meta={issues.length === 0 ? 'Clear' : `${issues.length} finding${issues.length === 1 ? '' : 's'}`}
      />

      <div className="insight-groups">
        {criticalIssues.length > 0 && (
          <IssueGroup
            tone="critical"
            icon={<XCircle size={18} />}
            title="Critical"
            summary={`${criticalIssues.length} confirmed high-risk interaction${criticalIssues.length === 1 ? '' : 's'}`}
            issues={criticalIssues}
          />
        )}

        {cautionIssues.length > 0 && (
          <IssueGroup
            tone="caution"
            icon={<AlertTriangle size={18} />}
            title="Caution"
            summary={`${cautionIssues.length} item${cautionIssues.length === 1 ? '' : 's'} need professional review`}
            issues={cautionIssues}
          />
        )}

        {(activeIngredientIssues.length > 0 || missingInstructionIssues.length > 0) && (
          <VerificationIssueGroup
            activeIngredientIssues={activeIngredientIssues}
            missingInstructionIssues={missingInstructionIssues}
          />
        )}

        {issues.length === 0 && (
          <div className={`clear-findings ${status}`}>
            <CheckCircle size={20} />
            <div>
              <strong>No confirmed dangerous interaction found</strong>
              <p>Keep following the prescription exactly as written and verify anything unclear with a professional.</p>
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
};

const IssueGroup: React.FC<{
  tone: 'critical' | 'caution';
  icon: ReactNode;
  title: string;
  summary: string;
  issues: SafetyIssue[];
}> = ({ tone, icon, title, summary, issues }) => (
  <div className={`issue-group ${tone}`}>
    <div className="issue-group-header">
      <div className="issue-group-icon">{icon}</div>
      <div>
        <h3>{title}</h3>
        <p>{summary}</p>
      </div>
    </div>
    <div className="issue-list">
      {issues.map((issue, index) => (
        <div className="issue-line" key={`${issue.title}-${index}`}>
          <span>{issue.title}</span>
          <p>{issue.description}</p>
        </div>
      ))}
    </div>
  </div>
);

const VerificationIssueGroup: React.FC<{
  activeIngredientIssues: SafetyIssue[];
  missingInstructionIssues: SafetyIssue[];
}> = ({ activeIngredientIssues, missingInstructionIssues }) => {
  const activeNames = activeIngredientIssues.flatMap((issue) => issue.medicines || []);
  const missingNames = missingInstructionIssues.flatMap((issue) => issue.medicines || []);

  return (
    <div className="issue-group verification">
      <div className="issue-group-header">
        <div className="issue-group-icon">
          <Info size={18} />
        </div>
        <div>
          <h3>Needs Verification</h3>
          <p>
            {activeIngredientIssues.length > 0
              ? `${activeIngredientIssues.length} medicine${activeIngredientIssues.length === 1 ? '' : 's'} need review`
              : 'Some instructions need manual confirmation'}
          </p>
        </div>
      </div>

      {activeNames.length > 0 && (
        <details className="verification-details" open={activeNames.length <= 4}>
          <summary>Active ingredient verification</summary>
          <div className="medicine-chip-list">
            {Array.from(new Set(activeNames)).map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
        </details>
      )}

      {missingNames.length > 0 && (
        <details className="verification-details">
          <summary>Missing dose, frequency, or duration</summary>
          <div className="medicine-chip-list">
            {Array.from(new Set(missingNames)).map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const MedicinesSection: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => (
  <motion.section
    className="report-section medicines-report-section"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.16 }}
  >
    <SectionHeader
      icon={<Pill size={20} />}
      title="Medicines Detected"
      subtitle="Review each medicine before taking it"
      meta={`${medicines.length} found`}
    />

    <div className="medicines-grid premium-grid">
      {medicines.map((medicine, index) => (
        <MedicineCard key={`${medicine.name}-${index}`} medicine={medicine} index={index} />
      ))}
    </div>
  </motion.section>
);

const PatientInstructionsSection: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => {
  const [language, setLanguage] = useState<InstructionLanguage>('en');
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const scheduleGroups = buildScheduleGroups(medicines);
  const contextClues = getPossiblePrescriptionContext(medicines);
  const checklist = getDoctorChecklist(medicines);

  const playAllInstructions = () => {
    const text = medicines
      .map((medicine, index) => `${index + 1}. ${medicine.name}. ${getInstructionText(medicine, language)}`)
      .join(' ');
    speakInstruction(text, language);
  };

  return (
    <motion.section
      className="report-section patient-instructions-section premium-guidance"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.14 }}
    >
      <div className="patient-guidance-header">
        <SectionHeader
          icon={<FileText size={20} />}
          title="Patient Instructions"
          subtitle="Daily guidance, voice support, QR labels, and doctor-review checklist"
          meta={`${medicines.length} instructions`}
        />
        <div className="instruction-toolbar">
          <div className="language-toggle" aria-label="Instruction language">
            <Languages size={16} />
            <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>English</button>
            <button className={language === 'bn' ? 'active' : ''} onClick={() => setLanguage('bn')}>বাংলা</button>
          </div>
          <button className="instruction-action primary" onClick={playAllInstructions}>
            <Volume2 size={16} />
            Play All Instructions
          </button>
        </div>
      </div>

      <TodayScheduleSummary groups={scheduleGroups} />
      <ScheduleGroups groups={scheduleGroups} />

      <div className="patient-instruction-list numbered">
        {medicines.map((medicine, index) => (
          <PatientInstructionItem
            key={`${medicine.name}-instruction-${index}`}
            medicine={medicine}
            index={index}
            language={language}
            activeLabel={activeLabel}
            onToggleLabel={(id) => setActiveLabel((current) => (current === id ? null : id))}
          />
        ))}
      </div>

      <PossiblePrescriptionContext clues={contextClues} />
      <DoctorDiscussionChecklist checklist={checklist} />
    </motion.section>
  );
};

void PatientInstructionsSection;

const TodayScheduleSummary: React.FC<{ groups: ScheduleGroup[] }> = ({ groups }) => {
  const summaryGroups = groups.filter((group) => group.medicines.length > 0);

  return (
    <div className="today-schedule-panel">
      <div className="today-schedule-title">
        <CalendarDays size={18} />
        <div>
          <h3>Today’s Simple Schedule</h3>
          <p>Use this as a quick guide, then confirm unclear items with your doctor or pharmacist.</p>
        </div>
      </div>
      <div className="today-schedule-grid">
        {summaryGroups.map((group) => (
          <div className="today-schedule-card" key={group.key}>
            <div className="schedule-card-icon">{group.icon}</div>
            <div>
              <span>{group.title}</span>
              <strong>{group.medicines.length} medicine{group.medicines.length === 1 ? '' : 's'}</strong>
              <p>{group.medicines.slice(0, 3).map((medicine) => medicine.name).join(', ')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScheduleGroups: React.FC<{ groups: ScheduleGroup[] }> = ({ groups }) => (
  <div className="schedule-groups-panel">
    {groups.map((group) => (
      <div className={`schedule-group-pill ${group.medicines.length === 0 ? 'empty' : ''}`} key={group.key}>
        <div className="schedule-group-title">
          {group.icon}
          <span>{group.title}</span>
        </div>
        <p>{group.subtitle}</p>
        <div className="schedule-group-chips">
          {group.medicines.length > 0 ? (
            group.medicines.slice(0, 6).map((medicine) => <span key={`${group.key}-${medicine.name}`}>{medicine.name}</span>)
          ) : (
            <span>No clear match</span>
          )}
        </div>
      </div>
    ))}
  </div>
);

const PatientInstructionItem: React.FC<{
  medicine: Medicine;
  index: number;
  language: InstructionLanguage;
  activeLabel: string | null;
  onToggleLabel: (id: string) => void;
}> = ({ medicine, index, language, activeLabel, onToggleLabel }) => {
  const instruction = getInstructionText(medicine, language);
  const labelId = `${medicine.name}-${index}`;
  const labelOpen = activeLabel === labelId;
  const qrText = buildQrLabelText(medicine, language);

  return (
    <article className="patient-instruction-item upgraded">
      <div className="instruction-number">{index + 1}</div>
      <div className="patient-instruction-body">
        <div className="patient-instruction-heading">
          <div>
            <h3>{medicine.name || 'Unnamed medicine'}</h3>
            <span>{medicine.generic_name || medicine.genericName || 'Active ingredient needs review'}</span>
          </div>
          <div className="instruction-badges">
            {getInstructionBadges(medicine).map((badge) => (
              <span className={`instruction-badge ${badge.tone}`} key={`${medicine.name}-${badge.label}`}>
                {badge.label}
              </span>
            ))}
          </div>
        </div>

        <div className="instruction-facts">
          <InstructionFact label="Form" value={displayValue(medicine.form)} />
          <InstructionFact label="Strength" value={displayValue(medicine.strength)} />
          <InstructionFact label="Schedule" value={getScheduleText(medicine)} />
          <InstructionFact label="Food timing" value={getFoodTimingText(medicine)} />
          <InstructionFact label="Duration" value={displayValue(medicine.durationLabel || medicine.duration, 'Needs review')} />
        </div>

        <div className={`primary-instruction-text ${language}`}>
          <span>{language === 'bn' ? 'নির্দেশনা' : 'Instruction'}</span>
          <p>{instruction}</p>
        </div>

        <div className="instruction-actions">
          <button className="instruction-action" onClick={() => speakInstruction(instruction, language)}>
            <Volume2 size={15} />
            Voice Guide
          </button>
          <button className="instruction-action" onClick={() => onToggleLabel(labelId)}>
            <QrCode size={15} />
            Printable / QR Label
          </button>
        </div>

        {labelOpen && (
          <div className="medicine-label-preview">
            <div className="medicine-label-card">
              <div>
                <span>MedCheck QR Label</span>
                <h4>{medicine.name}</h4>
                <p>{getScheduleText(medicine)} · {getFoodTimingText(medicine)}</p>
                <p>{displayValue(medicine.durationLabel || medicine.duration, 'Duration needs review')}</p>
              </div>
              <QRCodeSVG value={qrText} size={92} level="M" includeMargin />
            </div>
            <button className="instruction-action print" onClick={() => window.print()}>
              <Printer size={15} />
              Print Report
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const InstructionFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="instruction-fact">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const PossiblePrescriptionContext: React.FC<{ clues: ContextClue[] }> = ({ clues }) => (
  <div className="context-panel">
    <div className="subsection-heading">
      <Info size={18} />
      <div>
        <h3>Possible Prescription Context</h3>
        <p>This prescription may be related to the clues below. Confirm the actual condition with your doctor.</p>
      </div>
    </div>
    <div className="context-clue-grid">
      {clues.length > 0 ? (
        clues.map((clue) => (
          <div className="context-clue-card" key={clue.title}>
            <h4>{clue.title}</h4>
            <p>{clue.description}</p>
            <div className="medicine-chip-list compact">
              {clue.medicines.slice(0, 5).map((name) => <span key={`${clue.title}-${name}`}>{name}</span>)}
            </div>
          </div>
        ))
      ) : (
        <div className="context-clue-card">
          <h4>No clear context clue found</h4>
          <p>MedCheck did not infer a strong medicine-category clue from this prescription. Confirm the reason for treatment with your doctor.</p>
        </div>
      )}
    </div>
  </div>
);

const DoctorDiscussionChecklist: React.FC<{ checklist: Array<{ title: string; items: string[] }> }> = ({ checklist }) => (
  <div className="doctor-checklist-panel">
    <div className="subsection-heading">
      <ClipboardCheck size={18} />
      <div>
        <h3>Doctor Discussion Checklist</h3>
        <p>Screenshot this before your pharmacy or doctor follow-up.</p>
      </div>
    </div>
    <div className="doctor-checklist-grid">
      {checklist.map((section) => (
        <div className="doctor-checklist-card" key={section.title}>
          <h4>{section.title}</h4>
          {section.items.map((item) => (
            <label key={`${section.title}-${item}`}>
              <input type="checkbox" />
              <span>{item}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const LegacyPatientInstructionsSection: React.FC<{ medicines: Medicine[] }> = ({ medicines }) => (
  <motion.section
    className="report-section patient-instructions-section"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.14 }}
  >
    <SectionHeader
      icon={<FileText size={20} />}
      title="Patient Instructions"
      subtitle="Simple medicine-wise guidance from the prescription"
      meta={`${medicines.length} instructions`}
    />

    <div className="patient-instruction-list">
      {medicines.map((medicine, index) => (
        <LegacyPatientInstructionItem key={`${medicine.name}-instruction-${index}`} medicine={medicine} />
      ))}
    </div>
  </motion.section>
);

const LegacyPatientInstructionItem: React.FC<{ medicine: Medicine }> = ({ medicine }) => {
  const status = getMedicineStatus(medicine);
  const englishInstruction =
    medicine.englishInstruction ||
    medicine.instruction ||
    'Instruction needs review. Confirm dose, frequency, timing, and duration with a doctor or pharmacist.';
  const banglaInstruction =
    medicine.banglaInstruction ||
    'ডোজ, সময়, খাবারের আগে/পরে এবং কতদিন খাবেন তা ডাক্তার বা ফার্মাসিস্টের সাথে নিশ্চিত করুন।';

  return (
    <article className="patient-instruction-item">
      <div className="patient-instruction-heading">
        <div>
          <h3>{medicine.name || 'Unnamed medicine'}</h3>
          <span>{medicine.generic_name || medicine.genericName || 'Active ingredient needs review'}</span>
        </div>
        <span className={`medicine-status ${status.tone}`}>{status.label}</span>
      </div>
      <div className="instruction-language-grid">
        <div className="instruction-language-card">
          <span>English</span>
          <p>{englishInstruction}</p>
        </div>
        <div className="instruction-language-card bangla">
          <span>বাংলা</span>
          <p>{banglaInstruction}</p>
        </div>
      </div>
    </article>
  );
};

void LegacyPatientInstructionsSection;

const InteractionsSection: React.FC<{ interactions: DrugInteraction[] }> = ({ interactions }) => (
  <motion.section
    className="report-section interactions-report-section"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.22 }}
  >
    <SectionHeader
      icon={<AlertTriangle size={20} />}
      title="Interaction Review"
      subtitle="Confirmed and possible interactions from the verified list"
      meta={`${interactions.length} found`}
    />
    <div className="interactions-list">
      {interactions.map((interaction, index) => (
        <InteractionCard
          key={`${interaction.drug1}-${interaction.drug2}-${index}`}
          interaction={interaction}
          index={index}
        />
      ))}
    </div>
  </motion.section>
);

const NextStepsPanel: React.FC<{ tips: string[]; status: ReportStatus }> = ({ tips, status }) => {
  const isPositiveStatus = status === 'excellent-safe' || status === 'very-safe' || status === 'safe';
  const fallback =
    isPositiveStatus
      ? ['Continue only as prescribed.', 'Keep this medicine list for your next doctor or pharmacy visit.']
      : ['Check the highlighted items before use.', 'Confirm unclear medicine details with a doctor or pharmacist.'];
  const visibleTips = Array.from(new Set(tips.length > 0 ? tips : fallback)).slice(0, 4);

  return (
    <motion.section
      className="report-section next-steps-panel"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.28 }}
    >
      <SectionHeader
        icon={<Lightbulb size={20} />}
        title="Next Steps"
        subtitle="Clear actions before using this prescription"
      />
      <div className="next-step-list">
        {visibleTips.map((tip, index) => (
          <div className="next-step" key={`${tip}-${index}`}>
            <span>{index + 1}</span>
            <p>{tip}</p>
          </div>
        ))}
      </div>
    </motion.section>
  );
};

const DisclaimerPanel = () => (
  <section className="medical-disclaimer">
    <Info size={18} />
    <p>
      MedCheck is a patient education tool, not a medical diagnosis or prescription. Always confirm medicine
      instructions, active ingredients, and safety concerns with a licensed doctor or pharmacist.
    </p>
  </section>
);

const SectionHeader: React.FC<{
  icon: ReactNode;
  title: string;
  subtitle: string;
  meta?: string;
}> = ({ icon, title, subtitle, meta }) => (
  <div className="report-section-header">
    <div className="report-section-title">
      <div className="section-icon">{icon}</div>
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </div>
    {meta && <span className="section-meta">{meta}</span>}
  </div>
);

const LoadingAnalysis: React.FC<{
  currentStep: number;
  steps: Array<{ label: string; icon: ElementType }>;
  title: string;
  description: string;
}> = ({ currentStep, steps, title, description }) => (
  <div className="analysis-results">
    <motion.div className="loading-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="loading-visual">
        <motion.div
          className="loading-icon"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 size={48} />
        </motion.div>
        <motion.div
          className="loading-pulse"
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>

      <h2>{title}</h2>
      <p>{description}</p>

      <div className="progress-steps">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isComplete = index < currentStep;

          return (
            <motion.div
              key={step.label}
              className={`step-item ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="step-icon">{isComplete ? <CheckCircle size={18} /> : <Icon size={18} />}</div>
              <span>{step.label}</span>
              {isActive && <motion.div className="step-loader" initial={{ width: 0 }} animate={{ width: '100%' }} />}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  </div>
);

const AnalysisError: React.FC<{ error: string; onRetry: () => void; onManualEntry: () => void }> = ({
  error,
  onRetry,
  onManualEntry,
}) => (
  <div className="analysis-results">
    <motion.div className="error-container" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
      <div className="error-icon">
        <XCircle size={48} />
      </div>
      <h2>Analysis Issue</h2>
      <p className="error-message">{error}</p>

      <div className="error-tips">
        <h4>
          <Lightbulb size={18} />
          Tips for better results
        </h4>
        <div className="tips-list">
          <div className="tip-item">
            <Camera size={16} />
            <span>Use a clear, well-lit photo</span>
          </div>
          <div className="tip-item">
            <Focus size={16} />
            <span>Ensure text is in focus and readable</span>
          </div>
          <div className="tip-item">
            <Maximize2 size={16} />
            <span>Avoid tilted or skewed images</span>
          </div>
          <div className="tip-item">
            <Sun size={16} />
            <span>Minimize shadows and glare</span>
          </div>
        </div>
      </div>

      <div className="error-actions">
        <button className="btn btn-primary btn-lg" onClick={onRetry}>
          <Upload size={18} />
          Try Again
        </button>
        <button className="btn btn-secondary btn-lg" onClick={onManualEntry}>
          <Pill size={18} />
          Enter Medicines Manually
        </button>
      </div>
    </motion.div>
  </div>
);

const VerificationScreen: React.FC<{
  medicines: Medicine[];
  ocrText: string;
  onChange: (index: number, field: keyof Medicine, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onConfirm: () => void;
  onBack: () => void;
}> = ({ medicines, ocrText, onChange, onAdd, onRemove, onConfirm, onBack }) => (
  <div className="analysis-results">
    <div className="results-container">
      <motion.section className="report-section verification-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="verification-hero">
          <div className="verification-icon">
            <CheckCircle size={26} />
          </div>
          <div>
            <h2>Verify Medicine Details</h2>
            <p>Edit anything that looks wrong before MedCheck prepares the final safety report.</p>
          </div>
        </div>

        <div className="verification-list">
          {medicines.map((medicine, index) => (
            <div className="verification-card" key={`verify-${index}`}>
              <div className="verification-card-header">
                <span>Medicine {index + 1}</span>
                {medicines.length > 1 && (
                  <button className="icon-btn danger" onClick={() => onRemove(index)} aria-label="Remove medicine">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="verification-grid">
                <Field label="Medicine name" value={medicine.name} onChange={(value) => onChange(index, 'name', value)} />
                <Field label="Generic / active ingredient" value={medicine.generic_name || ''} onChange={(value) => onChange(index, 'generic_name', value)} />
                <Field label="Strength" value={medicine.strength || ''} onChange={(value) => onChange(index, 'strength', value)} />
                <Field label="Dose" value={medicine.dosage || ''} onChange={(value) => onChange(index, 'dosage', value)} />
                <Field label="Frequency" value={medicine.frequency || ''} onChange={(value) => onChange(index, 'frequency', value)} />
                <Field label="Timing" value={medicine.timing || ''} onChange={(value) => onChange(index, 'timing', value)} />
                <Field label="Duration" value={medicine.duration || ''} onChange={(value) => onChange(index, 'duration', value)} />
                <Field label="Instruction / raw schedule" value={medicine.rawInstruction || medicine.instruction || ''} onChange={(value) => onChange(index, 'rawInstruction', value)} wide />
                <Field label="Notes" value={medicine.notes || ''} onChange={(value) => onChange(index, 'notes', value)} wide />
              </div>
            </div>
          ))}
        </div>

        <button className="btn btn-outline add-medicine-btn" onClick={onAdd}>
          <Plus size={18} />
          Add Medicine
        </button>

        {ocrText && (
          <details className="ocr-review">
            <summary>View extracted prescription text</summary>
            <p>{ocrText}</p>
          </details>
        )}

        <div className="action-buttons">
          <button className="btn btn-outline btn-lg" onClick={onBack}>
            <Upload size={20} />
            Upload Again
          </button>
          <button className="btn btn-primary btn-lg" onClick={onConfirm}>
            <Sparkles size={20} />
            Confirm and Analyze
            <ChevronRight size={18} />
          </button>
        </div>
      </motion.section>
    </div>
  </div>
);

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  wide?: boolean;
}> = ({ label, value, onChange, wide }) => (
  <label className={`verification-field ${wide ? 'wide' : ''}`}>
    <span>{label}</span>
    <input value={value} onChange={(event) => onChange(event.target.value)} />
  </label>
);

function getMedicineStatus(medicine: Medicine): { label: string; tone: 'verified' | 'review' | 'lookalike' } {
  if (medicine.identificationStatus === 'identified') {
    return { label: 'Identified', tone: 'verified' };
  }

  if (medicine.identificationStatus === 'possible_lookalike') {
    return { label: 'Possible look-alike name', tone: 'lookalike' };
  }

  return { label: 'Needs review', tone: 'review' };
}

function displayValue(value?: string, fallback = 'Not provided'): string {
  return value && value.trim() ? value : fallback;
}

const MedicineCard: React.FC<{ medicine: Medicine; index: number }> = ({ medicine, index }) => {
  const confidence = medicine.confidence ? `${Math.round(medicine.confidence * 100)}%` : 'Not scored';
  const status = getMedicineStatus(medicine);
  const generic = medicine.generic_name || medicine.genericName || 'Needs review';
  const instruction =
    medicine.englishInstruction ||
    medicine.instruction ||
    'Instruction needs review. Confirm with a doctor or pharmacist before use.';

  return (
    <motion.article
      className="report-medicine-card"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      <div className="medicine-card-header">
        <div className="medicine-card-title-row">
          <div className="report-medicine-icon">
            <Pill size={18} />
          </div>
          <h3>{medicine.name || 'Unnamed medicine'}</h3>
        </div>
        <span className={`medicine-status ${status.tone}`}>{status.label}</span>
      </div>

      <div className="medicine-info-list">
        <MedicineInfoRow label="Generic / active ingredient" value={generic} highlight={medicine.identificationStatus === 'identified'} />
        <MedicineInfoRow label="Strength" value={displayValue(medicine.strength)} />
        <MedicineInfoRow label="Dose" value={displayValue(medicine.dosage)} />
        <MedicineInfoRow label="Frequency" value={displayValue(medicine.frequencyLabel || medicine.frequency)} />
        <MedicineInfoRow label="Timing" value={displayValue(medicine.timingLabel || medicine.timing)} />
        <MedicineInfoRow label="Duration" value={displayValue(medicine.durationLabel || medicine.duration)} />
        <MedicineInfoRow label="Instruction" value={instruction} />
        <MedicineInfoRow label="Confidence" value={confidence} />
        <MedicineInfoRow label="Status" value={status.label} />
      </div>

      {(medicine.commonUse || medicine.riskNotes) && (
        <div className="medicine-context">
          {medicine.commonUse && <p>{medicine.commonUse}</p>}
          {medicine.riskNotes && <p>{medicine.riskNotes}</p>}
        </div>
      )}

      {medicine.rawInstruction && (
        <div className="medicine-raw-instruction">
          <span>Original schedule</span>
          <p>{medicine.rawInstruction}</p>
        </div>
      )}
    </motion.article>
  );
};

const MedicineInfoRow: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`medicine-info-row ${highlight ? 'highlight' : ''}`}>
    <span>{label}</span>
    <p>{value}</p>
  </div>
);

const InteractionCard: React.FC<{ interaction: DrugInteraction; index: number }> = ({ interaction, index }) => {
  const confirmed = interaction.status === 'confirmed_interaction';

  return (
    <motion.article
      className={`interaction-card premium ${confirmed ? 'confirmed' : 'possible'}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <div className="interaction-heading">
        <span className={`state-badge ${confirmed ? 'confirmed_interaction' : 'possible_issue'}`}>
          {confirmed ? 'Confirmed interaction' : 'Possible issue'}
        </span>
        <span className="severity-text">{interaction.severity}</span>
      </div>
      <h3>
        {interaction.drug1} <span>+</span> {interaction.drug2}
      </h3>
      <p>{interaction.description}</p>
      <div className="interaction-recommendation">
        <Lightbulb size={14} />
        <span>{interaction.recommendation}</span>
      </div>
    </motion.article>
  );
};
